import isEqual from 'lodash/isEqual.js';
import type { EffectCallback } from 'react';
import { useEffect, useRef } from 'react';

/**
 * The useEffect wrapper that ensures that the imperative callback only fires once even if the
 * component re-renders.
 *
 * This is especially important to account for this for things like logging or registering dom
 * attachment.
 *
 * This is a requirement since React 18 in strict mode.
 *
 * Using a useRef is the suggested solution by the React team.
 *
 * See: https://github.com/reactwg/react-18/discussions/18 See:
 * https://github.com/reactwg/react-18/discussions/17
 */
export function useImperativeEffect(callback: EffectCallback, dependencies: unknown[]): void {
  const lastExecutionDependencies = useRef<unknown[] | null>(null);

  useEffect(() => {
    if (isEqual(lastExecutionDependencies.current, dependencies)) {
      return;
    }
    lastExecutionDependencies.current = dependencies;
    return callback();
    // We don't need to check dependencies here because we check them at the caller side (see eslintrc)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, dependencies);
}
